import type { ReactNode } from 'react';
import { AlertTriangle, type LucideIcon } from 'lucide-react';
import { ApiError } from '../lib/api';

/** Honest, helpful empty state: what this is, why it is empty, and the next action. */
export function EmptyState({
  title,
  children,
  action,
  icon: IconCmp,
  level = 2,
  compact = false,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  icon?: LucideIcon;
  /** 1 only for a page whose entire body is this empty state (no other h1 on the page). */
  level?: 1 | 2 | 3;
  compact?: boolean;
}) {
  const H = `h${level}` as const;
  return (
    <div
      className={
        compact
          ? 'flex flex-col items-start gap-2 rounded-md bg-paper-2 p-4'
          : 'flex flex-col items-start gap-3 rounded-lg border border-dashed border-rule-2 bg-sheet px-5 py-7 sm:px-8'
      }
    >
      {IconCmp ? (
        <span className="flex size-10 items-center justify-center rounded-full bg-paper-2 text-ink-2">
          <IconCmp size={20} strokeWidth={1.75} aria-hidden="true" />
        </span>
      ) : null}
      <H className={compact ? 'font-sans text-base font-semibold' : 'text-xl'}>{title}</H>
      {children ? <div className="max-w-prose text-[0.9375rem] text-ink-2">{children}</div> : null}
      {action ? <div className="flex flex-wrap gap-2 pt-1">{action}</div> : null}
    </div>
  );
}

/** Error block: what failed, the actionable message, the request id (for support), and a retry. */
export function ErrorBanner({
  title = 'This did not load.',
  message,
  error,
  onRetry,
}: {
  title?: string;
  message?: string;
  error?: unknown;
  onRetry?: () => void;
}) {
  const requestId =
    error instanceof ApiError ? error.requestId : null;
  const text =
    message ??
    (error instanceof Error ? error.message : error !== undefined ? String(error) : 'Unknown error.');
  const alreadyHasId = requestId ? text.includes(requestId) : false;
  return (
    <div
      role="alert"
      className="flex min-w-0 max-w-full items-start gap-3 rounded-md border border-stop/40 bg-stop-soft p-4 text-ink"
    >
      <AlertTriangle size={20} className="mt-0.5 flex-none text-stop" aria-hidden="true" />
      <div className="flex min-w-0 flex-col items-start gap-1.5">
        <p className="font-semibold">{title}</p>
        <p className="text-sm [overflow-wrap:anywhere]">{text}</p>
        {requestId && !alreadyHasId ? (
          <p className="font-mono text-xs text-ink-2">Request id: {requestId}</p>
        ) : null}
        {onRetry ? (
          <button type="button" className="fl-btn mt-1" data-size="sm" onClick={onRetry}>
            Retry
          </button>
        ) : null}
      </div>
    </div>
  );
}
